import type { LoanStatus } from '../../enums/loan-status.enum.js'
import type { CalendarDate } from '../../validators/calendar-date.validator.js'
import type { HpmlRollup } from '../compliance/evaluate.js'
import type { LoanIssueCode } from './codes.js'
import type { LoanIssueGroup } from './groups.js'
import type {
  LoanIssueBlockStatus,
  LoanIssuePhase,
} from './phases.js'
import type { LoanIssueField, LoanIssueSection } from './sections.js'
import type { TitleCommitmentReviewFacts } from '../title-commitment-review.js'
import type { InsuranceReviewFacts } from '../insurance-review.js'
import type { TridTiming } from '../trid/timing.js'

/** The four items on the pre-approval verification checklist. */
export type LoanIssueVerification = {
  creditReportReviewed: boolean
  incomeVerified: boolean
  cashToCloseVerified: boolean
  debtsAndAssetsVerified: boolean
}

/** Loan-to-value facts; amounts in dollars, CLTV as a fraction. */
export type LoanIssueLtv = {
  /** LE loan amount when an LE exists, else the pre-approval max loan. */
  loanAmount: number | null
  /** Lesser of the purchase price and the latest appraised value. */
  valueBasis: number | null
  valueBasisSource: 'PURCHASE_PRICE' | 'APPRAISAL' | null
  maxCltv: number | null
}

export type IssueDisplay = 'silent' | 'warn' | 'error'
export type IssueTransition = 'allow' | 'block'
/**
 * Who may proceed past a block: `lender` = lender staff of the loan's lender
 * (or table funding lender) and platform admins, with a reason; `never` =
 * nobody (TRID timing and disclosure gates).
 */
export type IssueOverride = 'lender' | 'never'

export const LOAN_ISSUE_SOURCES = ['CATALOG', 'UNDERWRITING'] as const
export type LoanIssueSource = (typeof LOAN_ISSUE_SOURCES)[number]

/** Returned-issue severity. `silent` stays on {@link IssueDisplay} only. */
export const LOAN_ISSUE_SEVERITIES = ['warn', 'error'] as const
export type LoanIssueSeverity = (typeof LOAN_ISSUE_SEVERITIES)[number]

export type LoanIssueDisplayMap = {
  readonly [P in LoanIssuePhase]: IssueDisplay
}
export type LoanIssueTransitionMap = {
  readonly [S in LoanIssueBlockStatus]: IssueTransition
}

export type LoanIssueEval =
  | { status: 'pass' }
  | {
      status: 'fail'
      actualLabel?: string
      requiredLabel?: string
    }
  | { status: 'unavailable'; reason: string }

export type LoanIssueContext = {
  status: LoanStatus
  phase: LoanIssuePhase
  loanProgramId: string | null
  /** Decimal fraction; null if income missing. */
  backEndDti: number | null
  /** Decimal fraction; null if the program has no cap. */
  programMaxBackEndDti: number | null
  preApprovalMaxLoan: number | null
  leLoanAmount: number | null
  /** null = no contract row; 0 = row exists with no sellers. */
  contractSellerCount: number | null
  /**
   * Current-file HPML rollup. null when compliance has not run (no occupancy /
   * no LE). Flip issues fire only when this is `'HPML'`.
   */
  hpml: HpmlRollup | null
  lastSaleDate: CalendarDate | null
  lastSalePrice: number | null
  contractDate: CalendarDate | null
  contractSalesPrice: number | null
  appraisalCount: number
  /** Latest title commitment plus expected review values. */
  titleCommitment: TitleCommitmentReviewFacts
  /** Selected policies, flood-zone flag, and program max deductibles. */
  insurance: InsuranceReviewFacts
  /** TRID dates, deadlines, and waiting periods (docs/trid-timing.md). */
  trid: TridTiming
  /** Pre-approval verification checklist; null without a pre-approval. */
  verification: LoanIssueVerification | null
  ltv: LoanIssueLtv
  preApprovalPurchasePrice: number | null
  /** Latest appraisal's value; null without one. */
  appraisedValue: number | null
  /** Open approval conditions by stage. */
  openConditions: { priorToDocs: number; priorToFunding: number }
  /** Borrowers qualified on bank-statement income with short coverage. */
  bankStatementCoverage: ReadonlyArray<{
    borrowerName: string
    monthsCovered: number
    windowMonths: number
  }>
  /** Tolerance on the current CD vs the baseline LE; null without both. */
  tolerance: { requiredCure: number; appliedCure: number; shortfall: number } | null
  /** Section A lines on the current LE that aren't in the APR. */
  leSectionAFeesNotInApr: readonly string[]
}

export type LoanIssueDef<S extends LoanIssueSection = LoanIssueSection> = {
  code: LoanIssueCode
  group: LoanIssueGroup
  label: string
  description: string
  section: S
  fields: readonly LoanIssueField<S>[]
  display: LoanIssueDisplayMap
  onTransition: LoanIssueTransitionMap
  whenUnavailable: IssueDisplay
  override: IssueOverride
  evaluate: (ctx: LoanIssueContext) => LoanIssueEval
  messageFail: (args: {
    actualLabel?: string
    requiredLabel?: string
  }) => string
  messageUnavailable: (args: { reason: string }) => string
}

/** Keeps `fields` typed to `section` at the call site. */
export function defineIssue<S extends LoanIssueSection>(
  def: LoanIssueDef<S>,
): LoanIssueDef<S> {
  return def
}
